import React from 'react';
import { AlertTriangle, Trash2 } from 'lucide-react';

/**
 * DeleteUserModal — type-to-confirm destructive user delete dialog.
 * Stateless; parent controls visibility via `target` and supplies the
 * typed-confirmation state + handlers. Extracted from Admin.jsx
 * .
 */
const DeleteUserModal = ({
  target,
  typeMatch, setTypeMatch,
  busy,
  onCancel,
  onConfirm,
}) => {
  if (!target) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80" onClick={() => !busy && onCancel()}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-enf-surface-deep border border-enf-role-warning/40 rounded-xl p-6" data-testid="delete-user-modal">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-md bg-enf-role-warning/15 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-5 h-5 text-enf-role-warning" />
          </div>
          <div className="flex-1">
            <div className="font-display text-2xl tracking-wide leading-none mb-1">Delete this user?</div>
            <div className="text-sm text-enf-text-secondary">
              This permanently deletes <span className="text-enf-text-primary font-semibold">{target.email}</span> and <span className="text-enf-role-warning font-semibold">all of their training data</span> — workouts, martial arts sessions, profile, painlog. Cannot be undone.
            </div>
          </div>
        </div>
        <div className="bg-enf-surface-mid border border-enf-surface-border rounded-md p-3 mb-4">
          <label className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2 block">
            Type <span className="font-mono text-enf-role-warning">{target.email}</span> to confirm
          </label>
          <input
            type="text" value={typeMatch}
            onChange={(e) => setTypeMatch(e.target.value)}
            autoFocus
            className="w-full px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded-md text-enf-text-primary text-sm font-mono focus:outline-none focus:border-enf-role-warning"
            data-testid="delete-confirm-input"
          />
        </div>
        <div className="flex gap-2">
          <button onClick={onCancel} disabled={busy}
            className="flex-1 px-4 py-2 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border rounded-md text-sm font-medium transition-colors"
            data-testid="delete-cancel">
            Cancel
          </button>
          <button onClick={onConfirm} disabled={busy || typeMatch !== target.email}
            className="flex-1 px-4 py-2 bg-enf-role-warning hover:bg-enf-role-warning-hover disabled:bg-enf-surface-elevated disabled:text-enf-text-muted disabled:cursor-not-allowed rounded-md text-sm font-semibold transition-colors flex items-center justify-center gap-2"
            data-testid="delete-confirm">
            <Trash2 className="w-4 h-4" /> {busy ? 'Deleting...' : 'Delete forever'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteUserModal;
